import { cn } from '@/utils/cn';
import { PrintButton } from '@/components/Buttons';
import { PrintLogo } from '@/components/Images';
import { NextScorecardButton } from '@/components/Score';
import { SimulationScore } from '@/components/Simulations';
import { SkeletonLoader } from '@/components/Loaders';
import { SlideoutModal } from '@/components/Modals';
import { TextContent, TextSplitMap } from '@/components/Text';
import { useScorecardModalFeature } from './hooks';
import { WORDS } from '@/constants/labels';
import React from 'react';

/**
 * Props for the SlideoutModalSimulationScore component.
 */
interface SlideoutModalSimulationScoreProps {
  /** The context in which the sidebar is being used, either 'Sales Representative' or 'Manager'. */
  context: 'Sales Representative' | 'Manager';
  /** Indicates if the sidebar is in a loading state. */
  isLoading?: boolean;
  /** Optional callback function to be called when the sidebar is closed. */
  onClose?: () => void;
}

/**
 * SlideoutModalSimulationScore Component
 *
 * This component renders a sidebar that displays the scorecard for a simulation. It includes
 * functionality for printing the scorecard, navigating to the next scorecard in the simulation
 * history, and closing the sidebar.
 */
export const SlideoutModalSimulationScore: React.FC<SlideoutModalSimulationScoreProps> = ({
  context,
  isLoading = false,
  onClose,
}) => {
  // ============================================================
  // Feature Hook
  // ============================================================
  const {
    certification,
    dateCompleted,
    handleClose,
    handleGetNextScoreCard,
    handleRetakeRolePlayClick,
    handleViewCertificateClick,
    isOpen,
    isPrinting,
    isXlUp,
    nextScoreCardExists,
    nextSimulation,
    printDate,
    printRef,
    printTitle,
    score,
    scorecardTransitionPhase,
    simulation,
    simulationSubtitleText,
    simulationTitleText,
    userName,
  } = useScorecardModalFeature({ context, onClose });

  /**
   * Determines if the scorecard is currently in a transition phase
   * (entering or exiting) to apply appropriate styles for animations. This is
   * used to disable interactions and apply transition classes when the scorecard
   * is transitioning in or out of view.
   */
  const isScorecardTransitioning = scorecardTransitionPhase !== 'idle';

  /**
   * Generates the class names for the card based on its current transition phase.
   * - When exiting, it applies classes to fade out and optionally translate the scorecard.
   * - When entering, it applies classes to start from a faded and translated state.
   * - When idle, it applies classes for the fully visible state.
   */
  const getTransitionClassName = (translateX?: boolean) => {
    // -- When exiting, we want to fade it out and optionally translate it to the left
    if (scorecardTransitionPhase === 'exiting') {
      return `opacity-0 ${translateX ? '-translate-x-2' : ''}`;
    }

    // -- When entering, we want to start it from a faded and optionally translated state
    if (scorecardTransitionPhase === 'entering') {
      return `opacity-0 ${translateX ? 'translate-x-2' : ''}`;
    }

    // -- When idle, it should be fully visible and in its normal position
    return `opacity-100 ${translateX ? 'translate-x-0' : ''}`;
  };

  /**
   * Combines the base transition classes with the dynamic classes based on the
   * transition phase. This ensures that the component has the necessary styles
   * for smooth animations when transitioning in and out of view, while also
   * respecting user preferences for reduced motion.
   */
  const transitionClassName = cn(
    'duration-200',
    'ease-out',
    'motion-reduce:transform-none',
    'motion-reduce:transition-none',
    'transform',
    'transition-all',
    'will-change-opacity',
    'will-change-transform',
    getTransitionClassName(true),
  );

  /**
   * Override component for the sidebar title.
   *
   * Displays the user's name and the date the score was completed for the manager context,
   * and just the date for the sales representative context. This provides a more personalized
   * and informative title area based on the context of the sidebar.
   */
  const TitleOverrideComponent = () => {
    // Class names for the title, applying consistent styling across contexts
    const classNames = [
      'font-text',
      'leading-section-subtitle',
      'text-neutral-gray-700',
      'text-section-subtitle',
      'whitespace-nowrap',
    ].join(' ');

    // Render the title based on the context, including the user's name for the
    // manager context and just the date for the sales representative context
    if (context === 'Manager') {
      const ariaLabel = `${userName}'s ${WORDS.SCORECARD} completed on ${dateCompleted}`;

      // When loading, show a skeleton loader that approximates the size of the title content
      if (isLoading) {
        return (
          <div className={transitionClassName}>
            <SkeletonLoader height="sm" width="2xl" rounded="none" />
          </div>
        );
      }

      // Render the user's name and the date completed for the manager context,
      // with an appropriate aria-label for accessibility
      return (
        <h2 aria-label={ariaLabel} className={cn(classNames, transitionClassName)}>
          <span className="font-bold">{userName}</span>
          <span className="mx-3">{`|`}</span>
          <time dateTime={dateCompleted}>{dateCompleted}</time>
        </h2>
      );
    }

    // For the sales representative context, we only display the
    // date completed without the user's name
    if (isLoading) {
      return (
        <div className={transitionClassName}>
          <SkeletonLoader height="sm" width="lg" rounded="none" />
        </div>
      );
    }

    // Render the date completed for the sales representative context,
    // with an appropriate aria-label for accessibility
    return (
      <h2
        aria-label={`Completed on ${dateCompleted}`}
        className={cn(classNames, transitionClassName)}
      >
        <time dateTime={dateCompleted}>{dateCompleted}</time>
      </h2>
    );
  };

  // ============================================================
  // Render
  // ============================================================
  return (
    <SlideoutModal
      closeButtonIconOnly={false}
      isOpen={isOpen}
      onClose={handleClose}
      titleOverride={<TitleOverrideComponent />}
      className={isOpen ? 'visible opacity-100' : 'invisible opacity-0'}
    >
      <div className={cn('flex flex-col p-4 lg:p-6', transitionClassName)} ref={printRef}>
        <PrintLogo isPrinting={isPrinting} />

        <div
          className={cn('mb-5 flex flex-col items-start md:flex-row', {
            'mb-1': isPrinting, // Reduce bottom margin when printing to save space
            'items-center justify-between': nextScoreCardExists,
            'items-start justify-start': isPrinting && nextScoreCardExists,
          })}
        >
          <TextContent
            className="!font-display text-body-title font-bold text-neutral-gray-800"
            isLoading={isLoading}
            label={isLoading ? ' ' : certification?.title}
            loadingClassName="w-1/3 h-[1.25rem]"
            testId="certification-title"
            type="paragraph"
          />

          {nextScoreCardExists && !isPrinting && (
            <div>
              <NextScorecardButton
                disabled={isScorecardTransitioning}
                onClick={handleGetNextScoreCard}
              />
            </div>
          )}
        </div>

        <div className={cn('mb-3 max-w-2xl', { 'max-w-full': isPrinting })}>
          <TextContent
            isLoading={isLoading}
            label={simulationTitleText}
            level={1}
            loadingLines={isXlUp ? 1 : 2}
            loadingClassName={cn('w-full h-[4rem]', isLoading ? (isXlUp ? '' : 'mt-0.5') : '')}
            testId="simulation-title"
            type="headline"
            variant="pageTitle"
            className={
              isPrinting
                ? 'text-section-title leading-section-title'
                : 'text-page-title leading-page-title'
            }
          />

          {isPrinting && (
            <p className="mt-1 font-text text-base font-bold text-neutral-gray-700">{userName}</p>
          )}
          {isPrinting && printDate && (
            <p className="font-text text-sm text-neutral-gray-700">{printDate}</p>
          )}
        </div>

        {simulationSubtitleText && (
          <div className="mb-3">
            <TextContent
              isLoading={isLoading}
              loadingLines={4}
              testId="simulation-subtitle"
              type="paragraph"
              variant="bodyMedium"
              weight="bold"
            >
              <TextSplitMap className="mb-0" tag="span" text={simulationSubtitleText} />
            </TextContent>
          </div>
        )}

        {!isPrinting && (
          <div className="mb-6">
            <PrintButton contentRef={printRef} documentTitle={printTitle} />
          </div>
        )}

        <SimulationScore
          certificationId={certification?.id.toString() || null}
          certificationTitle={certification?.title || undefined}
          complianceFlagText={score?.complianceReminders?.title}
          isLoading={isLoading}
          isPrinting={isPrinting}
          layout="compact"
          nextSimulation={nextSimulation}
          onRetakeRolePlayClick={handleRetakeRolePlayClick}
          onViewCertificateClick={handleViewCertificateClick}
          score={score}
          scoreIsProcessed={true}
          showHeader={false}
          showRetakeRolePlayButton={context === 'Sales Representative'}
          showScoreHistory={context === 'Sales Representative'}
          simulation={simulation}
          simulationId={score?.simulationId || simulation?.id.toString() || null}
        />
      </div>
    </SlideoutModal>
  );
};

SlideoutModalSimulationScore.displayName = 'SlideoutModalSimulationScore';

export default SlideoutModalSimulationScore;
